import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

dayjs.extend(customParseFormat);
dayjs.extend(quarterOfYear);

const DATE_PARSE_FORMATS = [
  'YYYY-MM-DD',
  'MM/DD/YYYY',
  'DD/MM/YYYY',
  'YYYY/MM/DD',
  'MM-DD-YYYY',
  'DD-MM-YYYY',
];

export const AGGREGATION_TYPES = {
  COUNT: 'count',
  SUM: 'sum',
};

/**
 * Parse a raw string value from parsedFieldValues to a number.
 * Strips thousand-separators, currency symbols, whitespace.
 */
export function parseMeasureValue(raw) {
  if (raw == null || raw === '') return 0;
  const cleaned = String(raw).replace(/[^0-9.\-]/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
}

/**
 * Try to parse a raw string as a date, returning a dayjs object or null.
 */
function parseDate(raw) {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    const d = dayjs(trimmed);
    if (d.isValid()) return d;
  }
  for (const fmt of DATE_PARSE_FORMATS) {
    const d = dayjs(trimmed, fmt, true);
    if (d.isValid()) return d;
  }
  return null;
}

/**
 * Generate a group key for a date value at the given granularity.
 */
export function dateGroupKey(raw, granularity) {
  const d = parseDate(raw);
  if (!d) return '—';
  switch (granularity) {
    case 'year':
      return d.format('YYYY');
    case 'quarter':
      return `Q${d.quarter()} ${d.format('YYYY')}`;
    case 'month':
      return d.format('MMMM YYYY');
    case 'week':
      return `W${d.week ? d.week() : Math.ceil(d.date() / 7)} ${d.format('YYYY')}`;
    case 'day':
      return d.format('YYYY-MM-DD');
    default:
      return d.format('MMMM YYYY');
  }
}

/**
 * Sort date group keys chronologically.
 */
function parseDateKeyForSort(key) {
  if (!key || key === '—') return Infinity;

  const monthNames = {
    January: 1, February: 2, March: 3, April: 4, May: 5, June: 6,
    July: 7, August: 8, September: 9, October: 10, November: 11, December: 12,
  };

  // "YYYY" pure year
  if (/^\d{4}$/.test(key)) return parseInt(key) * 10000;

  // "Q1 2025" quarter
  const qMatch = key.match(/^Q(\d)\s+(\d{4})$/);
  if (qMatch) return parseInt(qMatch[2]) * 10000 + parseInt(qMatch[1]) * 100;

  // "March 2025" month
  const mMatch = key.match(/^(\w+)\s+(\d{4})$/);
  if (mMatch && monthNames[mMatch[1]] != null) {
    return parseInt(mMatch[2]) * 10000 + monthNames[mMatch[1]] * 100;
  }

  // "2025-03-01" day
  if (/^\d{4}-\d{2}-\d{2}$/.test(key)) {
    return parseInt(key.replace(/-/g, ''));
  }

  return Infinity;
}

/**
 * Get the value of a dimension for a given record.
 * @param {Object} record - form instance record
 * @param {Object} dimension - { fieldId, isSystemField, dateGranularity? }
 * @returns {string} group key
 */
function getDimensionValue(record, dimension) {
  if (!dimension) return 'Total';

  if (dimension.isSystemField) {
    const raw = record[dimension.fieldId] || '';
    if (dimension.isDate && dimension.dateGranularity) {
      return dateGroupKey(raw, dimension.dateGranularity);
    }
    return raw || '—';
  }

  const raw = (record.parsedFieldValues || {})[dimension.fieldId];
  if (dimension.isDate && dimension.dateGranularity) {
    return dateGroupKey(raw, dimension.dateGranularity);
  }
  return (raw != null && raw !== '') ? String(raw) : '—';
}

/**
 * Compute pivot table data.
 *
 * @param {Array} dataSource - list of form instance records
 * @param {Object|null} rowDimension - { fieldId, isSystemField, isDate, dateGranularity }
 * @param {Object|null} colDimension - same shape
 * @param {Object} measure - { type: 'count'|'sum', fieldId?: string }
 * @returns {{ rows: string[], cols: string[], cells: Object, rowIsDate: boolean, colIsDate: boolean }}
 *   cells[rowKey][colKey] = { value: number, instanceIds: string[] }
 */
export function computePivot(dataSource, rowDimension, colDimension, measure) {
  const rowKeys = new Set();
  const colKeys = new Set();
  const cells = {};

  (dataSource || []).forEach((record) => {
    const rk = getDimensionValue(record, rowDimension);
    const ck = getDimensionValue(record, colDimension);
    rowKeys.add(rk);
    colKeys.add(ck);

    if (!cells[rk]) cells[rk] = {};
    if (!cells[rk][ck]) cells[rk][ck] = { value: 0, instanceIds: [] };

    const cell = cells[rk][ck];
    cell.instanceIds.push(record.id);

    if (measure.type === AGGREGATION_TYPES.COUNT) {
      cell.value += 1;
    } else if (measure.type === AGGREGATION_TYPES.SUM && measure.fieldId) {
      const raw = (record.parsedFieldValues || {})[measure.fieldId];
      cell.value += parseMeasureValue(raw);
    }
  });

  const rowIsDate = rowDimension?.isDate && rowDimension?.dateGranularity;
  const colIsDate = colDimension?.isDate && colDimension?.dateGranularity;

  const sortFn = (isDate) => (a, b) => {
    if (a === 'Total') return -1;
    if (b === 'Total') return 1;
    if (a === '—') return 1;
    if (b === '—') return -1;
    if (isDate) return parseDateKeyForSort(a) - parseDateKeyForSort(b);
    return a.localeCompare(b);
  };

  const rows = Array.from(rowKeys).sort(sortFn(rowIsDate));
  const cols = Array.from(colKeys).sort(sortFn(colIsDate));

  return { rows, cols, cells, rowIsDate, colIsDate };
}

/**
 * Compute row totals, column totals, and grand total.
 */
export function computeTotals(rows, cols, cells) {
  const rowTotals = {};
  const colTotals = {};
  let grandTotal = { value: 0, instanceIds: [] };

  rows.forEach((rk) => {
    rowTotals[rk] = { value: 0, instanceIds: [] };
    cols.forEach((ck) => {
      const cell = cells[rk]?.[ck];
      if (cell) {
        rowTotals[rk].value += cell.value;
        rowTotals[rk].instanceIds.push(...cell.instanceIds);
      }
    });
  });

  cols.forEach((ck) => {
    colTotals[ck] = { value: 0, instanceIds: [] };
    rows.forEach((rk) => {
      const cell = cells[rk]?.[ck];
      if (cell) {
        colTotals[ck].value += cell.value;
        colTotals[ck].instanceIds.push(...cell.instanceIds);
      }
    });
  });

  rows.forEach((rk) => {
    grandTotal.value += rowTotals[rk].value;
    grandTotal.instanceIds.push(...rowTotals[rk].instanceIds);
  });

  return { rowTotals, colTotals, grandTotal };
}

const isNumericMeasureType = (inputType) => {
  const t = (inputType || '').toLowerCase();
  return t === 'number' || t === 'currency';
};

/**
 * From field display settings across multiple records, extract fields
 * categorised as dimensions or measures.
 * 維度：僅 showInList 的欄位。度量：所有數字/金額欄位（含未勾選列表中顯示），方便完整分析。
 */
export function classifyFields(dataSource) {
  const fieldMap = new Map();
  const measureCandidateMap = new Map();

  (dataSource || []).forEach((record) => {
    let settings = record.fieldDisplaySettings || [];
    if (typeof settings === 'string') {
      try { settings = JSON.parse(settings); } catch { settings = []; }
    }
    if (!Array.isArray(settings)) settings = [];

    settings.filter((f) => f.showInList).forEach((field) => {
      if (!fieldMap.has(field.fieldId)) {
        const displayLabel = field.displayLabel || field.originalLabel || field.DisplayLabel || field.OriginalLabel || field.fieldId;
        fieldMap.set(field.fieldId, {
          fieldId: field.fieldId,
          displayLabel,
          originalLabel: field.originalLabel || field.OriginalLabel || displayLabel,
          inputType: (field.inputType || field.InputType || '').toLowerCase(),
          fieldType: field.fieldType || field.FieldType || '',
          order: field.order ?? field.Order ?? 0,
        });
      }
    });

    settings.forEach((field) => {
      const inputType = (field.inputType || field.InputType || '').toLowerCase();
      if (!isNumericMeasureType(inputType)) return;
      if (measureCandidateMap.has(field.fieldId)) return;
      const displayLabel = field.displayLabel || field.originalLabel || field.DisplayLabel || field.OriginalLabel || field.fieldId;
      measureCandidateMap.set(field.fieldId, {
        fieldId: field.fieldId,
        displayLabel,
        originalLabel: field.originalLabel || field.OriginalLabel || displayLabel,
        inputType,
        fieldType: field.fieldType || field.FieldType || '',
        order: field.order ?? field.Order ?? 0,
      });
    });
  });

  const allFields = Array.from(fieldMap.values()).sort((a, b) => a.order - b.order);
  const measureFields = Array.from(measureCandidateMap.values()).sort((a, b) => a.order - b.order);
  const dateFields = allFields.filter((f) => (f.inputType || '').toLowerCase() === 'date');
  const dimensionFields = allFields;

  return { allFields, measureFields, dateFields, dimensionFields };
}
